import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

// deep import (not the feature barrel) to avoid a trading<->custodial import cycle.
import { useCustodialPanelState } from '@/features/custodial/hooks/useCustodialPanelState';
import { useTokenPairStore } from '@/features/tokenPairs';
import { useSpeedBackend } from '@/features/wallet';
import { queryKeys } from '@/lib/query';

import { useTradingStore } from '../store';
import { isActionableActiveTrade } from '../utils/activeTrades';

// Resting is ACTIVE: the order holds the pair's slot and the user can still act
// on it (cancel / fill now), so it belongs in the same list as Open/Pending.
export const ACTIVE_STATUSES = 'Open,Pending,Resting';

const ACTIVE_LIMIT = 20;

// Status changes arrive over the trade-status websocket, which invalidates this query
// (useTradeNotificationHandler). Polling is only a backstop for what the push can't
// cover — a silently stalled socket, or a server-side write that never published.
//
// Not zero even when connected: a socket that stops delivering without dropping looks
// exactly like an idle one, and that is the failure this interval exists to bound.
const CONNECTED_REFETCH_INTERVAL_MS = 30_000;
// no push path at all, so this is the only way status changes arrive.
const DISCONNECTED_REFETCH_INTERVAL_MS = 1_000;
// long enough that remounting a consumer (opening the mobile panel, switching tabs)
// reuses the cached list instead of refetching; short enough to stay a backstop.
export const ACTIVE_STALE_TIME_MS = 5_000;

/**
 * How often to poll the active-trades list, given what else is feeding it.
 *
 * Exported so the cadence is pinned by a test rather than being an inline ternary that
 * silently regresses to a hot poll — which is what it was before the push drove it.
 */
export function activeTradesRefetchInterval(input: {
    custodialPanelOpen: boolean;
    streamConnected: boolean;
}): number | false {
    if (input.custodialPanelOpen) return false;
    return input.streamConnected
        ? CONNECTED_REFETCH_INTERVAL_MS
        : DISCONNECTED_REFETCH_INTERVAL_MS;
}

/**
 * fetches the authenticated user's actionable active (Open/Pending/Resting)
 * trades from the API.
 *
 * only enabled in real mode with a valid auth token. returns an empty
 * array while disabled so callers don't need null-checks.
 *
 * close-submitted rows still reconcile to terminal history server-side, but
 * they no longer block the hot open path or active-trade UI.
 */
export function useActiveUserTrades() {
    const isRealMode = useTradingStore((s) => s.runtimeMode === 'real');
    const token = useTradingStore((s) => s.token);
    const networkId = useTokenPairStore((s) => s.networkId);
    // trades history moved to factions.backend's speed facet (read-only, V1).
    const backend = useSpeedBackend();
    // pause background polling while the Quick Trade Wallet modal is open.
    const custodialPanelOpen = useCustodialPanelState((s) => s.isOpen);
    const streamConnected = useTradingStore((s) => s.tradeStreamConnected);

    const enabled =
        isRealMode && !!token && backend !== null && networkId !== null;

    return useQuery({
        queryKey: queryKeys.trades.active(networkId ?? 0),
        queryFn: async () => {
            if (!backend || networkId === null) return [];
            const trades = await backend.fetchUserTrades({
                networkId,
                status: ACTIVE_STATUSES,
                limit: ACTIVE_LIMIT,
            });
            return trades.filter(isActionableActiveTrade);
        },
        enabled,
        staleTime: ACTIVE_STALE_TIME_MS,
        refetchInterval: activeTradesRefetchInterval({
            custodialPanelOpen,
            streamConnected,
        }),
    });
}

/**
 * `true` when the server lists an active trade that this client has no slot for.
 *
 * It used to mean "any trade other than the single one being managed", which
 * made a legitimate second position — BTC open, now opening ETH — read as a
 * conflict and disabled trading outright. The store holds a slot per coin now,
 * and `useSyncActiveTradesToStore` fills one for every row in this list, so the
 * honest question is whether anything in the list is genuinely untracked.
 *
 * In practice that should be transient (one render between the list landing and
 * the sync effect running) or a real fault, which is why nothing gates opening
 * on it any more — it is diagnostic, not a lock.
 */
export function useHasUnmanagedActiveTrades(): boolean {
    const positionsByCoin = useTradingStore((s) => s.positionsByCoin);
    const { data: activeTrades } = useActiveUserTrades();

    return useMemo(() => {
        if (!activeTrades || activeTrades.length === 0) return false;
        const tracked = new Set(
            Object.values(positionsByCoin)
                .map((slot) => slot.tradeHash)
                .filter((hash): hash is string => hash !== null),
        );
        return activeTrades.some((t) => !tracked.has(t.tradeHash));
    }, [activeTrades, positionsByCoin]);
}
